import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { STORE } from "@/lib/store";

function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Staff Login | Knoble Rides";
    if (!loading && user) navigate("/admin/dashboard");
  }, [user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0B0A09] px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="mx-auto mb-6 flex w-fit items-center rounded-lg bg-white px-3 py-2">
          <img src={STORE.logo} alt="Knoble Rides" className="h-9 w-auto" />
        </Link>
        <div className="rounded-2xl border border-white/10 bg-[#161412] p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#8C5A32]">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-white">Staff Login</h1>
              <p className="text-sm text-zinc-400">Manage bikes & gallery</p>
            </div>
          </div>

          <form data-testid="admin-login-form" onSubmit={submit} className="mt-7 space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-zinc-300">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0B0A09] px-4 py-3 text-white outline-none ring-[#8C5A32] focus:ring-2"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-zinc-300">Password</label>
              <input
                data-testid="admin-login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-[#0B0A09] px-4 py-3 text-white outline-none ring-[#8C5A32] focus:ring-2"
                placeholder="••••••••"
              />
            </div>
            {error && <p data-testid="admin-login-error" className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</p>}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={submitting}
              className="btn-brown flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
        <Link to="/" className="mt-6 block text-center text-sm text-zinc-500 hover:text-zinc-300">← Back to website</Link>
      </div>
    </div>
  );
}
